import type {
  Link as LinkNode,
  Literal,
  Node,
  Parent,
  Root,
  RootContent,
  Text as TextNode,
} from "mdast";

/**
 * Plugin to convert <br> tags to line breaks.
 * This is needed because we do not render HTML tags in the markdown response.
 * And markdown tables do not normally support line breaks, so AI responses often use
 * <br> tags to indicate line breaks.
 */
export function LineBreakPlugin() {
  return createReplaceTreePlugin((node) => {
    if (node.type === "html" && isLiteral(node) && textIsBr(node.value)) {
      return [{ type: "break" }];
    }
    return null;
  });
}

const CITATION_LINK_PREFIX = "https://citation-";

/**
 * @returns citation link index (e.g. "1", "2") if this is a citation link, undefined otherwise.
 */
export function parseCitationLink(link: string): string | undefined {
  if (!link.startsWith(CITATION_LINK_PREFIX)) {
    return undefined;
  }
  return link.slice(CITATION_LINK_PREFIX.length);
}

/**
 * Plugin to convert citations to <citation> tags.
 * Citations are formatted as [^1], [^2], etc.
 * Citations previously used to be formatted as [c1], [c2], etc.
 */
export function CitationPlugin() {
  return createReplaceTreePlugin((node) => {
    if (!(node.type === "text" && isLiteral(node))) {
      return null; // only look at text nodes
    }
    // find all citations in the text
    let remainingText = node.value;
    let foundCitation = findNextCitation(remainingText);
    if (!foundCitation) {
      return null; // no-op if no citations found
    }

    const parts: (TextNode | LinkNode)[] = [];
    while (foundCitation) {
      // create a node for each citation + preserve preceding text
      if (foundCitation.position > 0) {
        parts.push({
          type: "text",
          value: remainingText.slice(0, foundCitation.position),
        });
      }
      parts.push({
        type: "link",
        children: [
          {
            type: "text",
            value: foundCitation.citationIndex,
          },
        ],
        url: `${CITATION_LINK_PREFIX}${foundCitation.citationIndex}`,
      });
      remainingText = remainingText.slice(
        foundCitation.position + foundCitation.length,
      );
      foundCitation = findNextCitation(remainingText);
    }
    // preserve trailing text as well
    if (remainingText) {
      parts.push({
        type: "text",
        value: remainingText,
      });
    }
    return parts;
  });
}

function isParent(node: Node): node is Parent {
  return "children" in node;
}

function isLiteral(node: Node): node is Literal {
  return "value" in node;
}

function textIsBr(text: string) {
  return text.trim().match(/^<br\s*\/?>$/);
}

/**
 * Iterate over all nodes in the tree, calling the replacer function for each node.
 * For each node that returns non-null, replace the node with the returned nodes.
 *
 * @param root - The root node of the tree. The tree is modified in place.
 * @param replacer - A function that takes a node and returns an array of nodes to replace it with,
 * or null if no replacement is needed.
 */
export function createReplaceTreePlugin(
  replacer: (node: Node) => RootContent[] | null,
) {
  return (root: Root) => {
    function visit(node: Node, index: number, parent: Parent) {
      const replacements = replacer(node);
      if (replacements) {
        parent.children = [
          ...parent.children.slice(0, index),
          ...replacements,
          ...parent.children.slice(index + 1),
        ];
      }
      if (isParent(node) && node.children) {
        node.children.forEach((child: Node, i: number) =>
          visit(child, i, node),
        );
      }
    }
    root.children.forEach((child: Node, i: number) => visit(child, i, root));
  };
}

interface FoundCitation {
  position: number; // start position of the citation in the text
  length: number; // length of the citation, e.g. [^1] -> 4
  citationIndex: string; // the index in the citation, e.g. [^1] -> "1"
}

// citations are formatted as [^1], [^2], etc or [c1], [c2], etc
function findNextCitation(text: string): FoundCitation | undefined {
  const citationRegex = /\[(c\d+|\^\d+)\]/g;
  const match = citationRegex.exec(text);
  if (match) {
    return {
      position: match.index,
      length: match[0].length,
      citationIndex: match[1].replace(/^\^/, ""), // Only remove leading ^, not leading c
    };
  }
  return undefined;
}

interface FoundResultList {
  position: number; // start position of the results list in the text
  length: number; // length of the results list, e.g. [results:1,2,3] -> 17
  idNumbers: string[]; // the id numbers in the results list, e.g. [results:1,2,3] -> ["1", "2", "3"]
}

interface FoundMediaList extends FoundResultList {
  position: number;
  length: number;
  idNumbers: string[];
}

// Finds the next results list in the text.
// Results lists are formatted as [results:1,2,3], [results:4,5], etc.
// The id numbers are extracted from the list and returned as an array of strings
function findNextResultList(text: string): FoundResultList | undefined {
  const resultsRegex = /\[results:(?:\d+,)*\d+\]/g;
  const match = resultsRegex.exec(text);
  if (match) {
    const split = match[0].split(":")[1];
    const nums = split.split(",").map((s) => s.trim().replace("]", ""));
    return {
      position: match.index,
      length: match[0].length,
      idNumbers: nums,
    };
  }
  return undefined;
}

// Finds the next results list in the text.
// Results lists are formatted as [results:1,2,3], [results:4,5], etc.
// The id numbers are extracted from the list and returned as an array of strings
function findNextMediaList(text: string): FoundMediaList | undefined {
  const resultsRegex = /\[media:(?:\d+,)*\d+\]/g;
  const match = resultsRegex.exec(text);
  if (match) {
    const split = match[0].split(":")[1];
    const nums = split.split(",").map((s) => s.trim().replace("]", ""));
    return {
      position: match.index,
      length: match[0].length,
      idNumbers: nums,
    };
  }
  return undefined;
}

export function ResultsPlugin() {
  return createReplaceTreePlugin((node) => {
    if (!(node.type === "text" && isLiteral(node))) {
      return null;
    }
    let remainingText = node.value;
    let foundResultsList = findNextResultList(remainingText);
    if (!foundResultsList) {
      return null;
    }

    // TODO: use custom react component for result lists: OSE-5623
    const parts: TextNode[] = [];
    while (foundResultsList) {
      if (foundResultsList.position > 0) {
        parts.push({
          type: "text",
          value: remainingText.slice(0, foundResultsList.position),
        });
      }
      parts.push({
        type: "text",
        value: foundResultsList.idNumbers.join(","),
      });
      remainingText = remainingText.slice(
        foundResultsList.position + foundResultsList.length,
      );
      foundResultsList = findNextResultList(remainingText);
    }
    if (remainingText) {
      parts.push({
        type: "text",
        value: remainingText,
      });
    }
    return parts;
  });
}

export function MediaPlugin() {
  return createReplaceTreePlugin((node) => {
    if (!(node.type === "text" && isLiteral(node))) {
      return null;
    }

    let remainingText = node.value;
    let foundMediaList = findNextMediaList(remainingText);
    if (!foundMediaList) {
      return null;
    }

    // TODO: use custom react component for media lists: OSE-5582
    const parts: TextNode[] = [];
    while (foundMediaList) {
      if (foundMediaList.position > 0) {
        parts.push({
          type: "text",
          value: remainingText.slice(0, foundMediaList.position),
        });
      }
      parts.push({
        type: "text",
        value: foundMediaList.idNumbers.join(","),
      });
      remainingText = remainingText.slice(
        foundMediaList.position + foundMediaList.length,
      );
      foundMediaList = findNextResultList(remainingText);
    }
    if (remainingText) {
      parts.push({
        type: "text",
        value: remainingText,
      });
    }
    return parts;
  });
}
